import {
  FileUploadActionTypes,
  IFileUploadSuccessAction,
} from '@app/components/FileUpload/modules/fileUpload'
import { AssociatedContact } from '@app/models/AssociatedContact'
import { Attachment } from '@app/models/Attachment'
import {
  DevelopmentClient,
  IDevelopmentClient,
} from '@app/models/DevelopmentClient'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface IClientEditState {
  readonly canSave: boolean
  readonly client: IDevelopmentClient
}

const StateRecord = Record({
  canSave: false,
  client: undefined,
})

export class ClientEditState extends StateRecord implements IClientEditState {
  public static fromJS(initialState?: Partial<IClientEditState>) {
    return new ClientEditState(initialState)
  }

  public readonly canSave: boolean
  public readonly client: DevelopmentClient

  constructor(props?: Partial<IClientEditState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  LoadClient = 'CLIENT_EDIT::LOAD_CLIENT',
  LoadClientSuccess = 'CLIENT_EDIT::LOAD_CLIENT_SUCCESS',
  ResetState = 'CLIENT_EDIT::RESET_STATE',
  SaveClient = 'CLIENT_EDIT::SAVE_CLIENT',
  SaveClientFailure = 'CLIENT_EDIT::SAVE_CLIENT_FAILURE',
  SaveClientSuccess = 'CLIENT_EDIT::SAVE_CLIENT_SUCCESS',
  SetContacts = 'CLIENT_EDIT::SET_CONTACTS',
  SetLogo = 'CLIENT_EDIT::SET_LOGO',
  SetName = 'CLIENT_EDIT::SET_NAME',
  GetClients = 'CLIENT_EDIT::GET_CLIENTS',
  SetClients = 'CLIENT_EDIT::SET_CLIENTS',
}

interface ILoadClientAction {
  type: ActionTypes.LoadClient
  value?: string
}

interface ILoadClientSuccessAction {
  type: ActionTypes.LoadClientSuccess
  value: DevelopmentClient
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISaveClientAction {
  type: ActionTypes.SaveClient
  value?: string
}

interface ISaveClientFailureAction {
  type: ActionTypes.SaveClientFailure
  error: string
}

interface ISaveClientSuccessAction {
  type: ActionTypes.SaveClientSuccess
}

interface ISetContactsAction {
  type: ActionTypes.SetContacts
  value: List<AssociatedContact>
}

interface ISetNameAction {
  type: ActionTypes.SetName
  value: string
}

interface ISetLogoAction {
  type: ActionTypes.SetLogo
  attachment: Attachment
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadClientAction
  | ILoadClientSuccessAction
  | IResetStateAction
  | ISaveClientAction
  | ISaveClientFailureAction
  | ISaveClientSuccessAction
  | ISetContactsAction
  | ISetLogoAction
  | ISetNameAction

// -------------------------
// ACTION CREATORS
// -------------------------

function loadClient(value?: string): ILoadClientAction {
  return { type: ActionTypes.LoadClient, value }
}

function loadClientSuccess(value: DevelopmentClient): ILoadClientSuccessAction {
  return {
    type: ActionTypes.LoadClientSuccess,
    value,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function save(value?: string): ISaveClientAction {
  return {
    type: ActionTypes.SaveClient,
    value,
  }
}

function saveFailure(error: string): ISaveClientFailureAction {
  return {
    error,
    type: ActionTypes.SaveClientFailure,
  }
}

function saveSuccess(): ISaveClientSuccessAction {
  return {
    type: ActionTypes.SaveClientSuccess,
  }
}

function setContacts(value: List<AssociatedContact>): ISetContactsAction {
  return {
    type: ActionTypes.SetContacts,
    value,
  }
}

function setName(value: string): ISetNameAction {
  return {
    type: ActionTypes.SetName,
    value,
  }
}

function setClientLogo(attachment: Attachment): ISetLogoAction {
  return {
    attachment,
    type: ActionTypes.SetLogo,
  }
}

export const actionCreators = {
  loadClient,
  loadClientSuccess,
  resetState,
  save,
  setContacts,
  setName,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<ClientEditState> = (
  state: ClientEditState = new ClientEditState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction

  switch (action.type) {
    case ActionTypes.LoadClient:
      if (!action.value) {
        const newContact = new DevelopmentClient()
        return state.set('client', newContact) as ClientEditState
      } else {
        return state as ClientEditState
      }

    case ActionTypes.LoadClientSuccess:
      return state
        .set('client', action.value)
        .set('canSave', true) as ClientEditState

    case ActionTypes.ResetState:
      return state
        .set('canSave', false)
        .set('client', undefined) as ClientEditState

    case ActionTypes.SaveClient:
      return state as ClientEditState

    case ActionTypes.SaveClientFailure:
      return state as ClientEditState

    case ActionTypes.SaveClientSuccess:
      return state as ClientEditState

    case ActionTypes.SetContacts:
      return state.setIn(
        ['client', 'contacts'],
        action.value,
      ) as ClientEditState

    case ActionTypes.SetName:
      return state.setIn(['client', 'name'], action.value) as ClientEditState

    case ActionTypes.SetLogo:
      return state.setIn(
        ['client', 'logo'],
        action.attachment,
      ) as ClientEditState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* getClientLogoSaga(action: IFileUploadSuccessAction) {
  try {
    const client = yield select(selectClient())

    if (client && action.file.key === 'client-profile-image-editor') {
      yield put(setClientLogo(action.attachment))
    }
  } catch (err) {
    console.error(err)
  }
}

function* loadClientSaga(action: ILoadClientAction) {
  try {
    if (action.value) {
      const baseUrl = `/api/DevelopmentClients/${action.value}`

      const result = yield makeAuthenticatedRequest(baseUrl)

      if (result.success === true) {
        yield put(resetState())
        yield put(loadClientSuccess(DevelopmentClient.fromJS(result.entity)))
      } else {
        yield put(errorToast('Oops!', result.message))
      }
    }
  } catch (err) {
    console.error(err)
  }
}

function* saveClientSaga(action: ISaveClientAction) {
  try {
    const state = yield select(selectModuleState())

    const baseUrl = action.value
      ? `/api/DevelopmentClients/${action.value}`
      : '/api/DevelopmentClients'

    let photos = yield select(selectAttachments('development-client-photos'))
    if (photos) {
      photos = photos.valueSeq().map((item: any) => {
        if (!item.error) {
          return item.attachment
        }

        return false
      })
    } else {
      photos = []
    }

    let attachments = yield select(
      selectAttachments('development-client-attachments'),
    )
    if (attachments) {
      attachments = attachments.valueSeq().map((item: any) => {
        if (!item.error) {
          return item.attachment
        }

        return false
      })
    } else {
      attachments = []
    }

    const client = state.client.toJS()
    client.photos = photos
    client.attachments = attachments

    const clientOpts = {
      body: JSON.stringify(client),
      method: action.value ? RequestMethod.PUT : RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(baseUrl, clientOpts)

    if (result.success === true) {
      yield put(saveSuccess())
      yield put(resetState())
      yield put(
        successToast(
          'Success',
          action.value
            ? `${state.client.name}'s information updated`
            : 'New Client Created',
        ),
      )
      yield put(push(`/development/client/${result.entity.slug}`))
    } else {
      yield put(saveFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchFileUpload() {
  yield takeLatest(FileUploadActionTypes.FileUploadSuccess, getClientLogoSaga)
}

function* watchLoadClient() {
  yield takeLatest(ActionTypes.LoadClient, loadClientSaga)
}

function* watchSaveClient() {
  yield takeLatest(ActionTypes.SaveClient, saveClientSaga)
}

export function* clientEditRootSaga() {
  yield fork(watchLoadClient)
  yield fork(watchSaveClient)
  yield fork(watchFileUpload)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): ClientEditState =>
  state.clientEdit
const selectFileUploadState = (state: ApplicationState) => state.fileUpload

export const selectAttachments = (key: string) =>
  createSelector(selectFileUploadState, (mState) => {
    return mState.files.get(key)
  })

export const selectCanSave = () =>
  createSelector(selectModuleState(), (mState) => mState.canSave)

export const selectClient = () =>
  createSelector(selectModuleState(), (mState) => mState.client)
